import { type ChangeEventHandler, type FocusEventHandler, forwardRef } from 'react';

import { twMergeClsx } from 'utils/tw-with-clsx';

const inputStyles =
  'transition-colors px-1 md:px-0 align-middle bg-[transparent] text-dark-gray-200 border-b w-full resize-none h-9 border-base-gray-500 outline-none text-lg leading-7 pb-[10px] hover:border-base-dark-gray-200 disabled:border-base-gray-500 focus:border-base-dark-gray-200 placeholder:text-base-gray-600/50';
const inputErrorStyles =
  'border-base-red-100 hover:border-base-red-100 focus:border-base-red-100 text-base-red-100';
const inputMobileStyles =
  'bg-base-gray-700 rounded-xsmall h-10 text-sm font-normal leading-6 px-3 w-full outline-none py-[7px]';

interface TextAreaPropTypes {
  error?: boolean | string | null;
  label?: string;
  upperLabel?: boolean;
  placeholder?: string;
  required?: boolean;
  containerClassName?: string;
  name: string;
  id?: string;
  onChange?: ChangeEventHandler<HTMLTextAreaElement>;
  value?: string | number;
  inputClassName?: string;
  disabled?: boolean;
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;
  isMobile?: boolean;
}

type InputRefType = HTMLTextAreaElement;

const TextArea = forwardRef<InputRefType, TextAreaPropTypes>((props, ref) => {
  const {
    placeholder,
    inputClassName,
    error,
    containerClassName,
    upperLabel,
    label,
    name,
    id,
    required,
    value,
    onChange,
    onFocus,
    disabled,
    isMobile,
  } = props;

  const inputClassess = twMergeClsx(
    isMobile ? inputMobileStyles : inputStyles,
    inputClassName,
    error && inputErrorStyles,
  );

  return (
    <div className={twMergeClsx('text-base-gray-600 relative', containerClassName)}>
      {label ? (
        <label
          className={twMergeClsx(
            'text-sm md:text-[10px] leading-3 font-semibold md:font-normal mb-2 gap-1 flex items-center text-base-gray-800 font-nunito whitespace-nowrap',
            upperLabel && 'md:uppercase font-semibold md:tracking-[2px] mb-2 md:mb-1',
            error && 'text-base-red-100',
          )}
          htmlFor={name || id}
        >
          {label}
          {required && <span className="text-base-red-100 opacity-50">*</span>}
        </label>
      ) : null}
      <textarea
        ref={ref}
        name={name || id}
        id={name || id}
        placeholder={placeholder}
        className={inputClassess}
        onChange={onChange}
        value={value}
        disabled={disabled}
        onFocus={onFocus}
      />
      {typeof value === 'string' && error && typeof error === 'string' && (
        <div className="text-base-red-100 text-[10px] leading-3 lowercase whitespace-nowrap absolute -bottom-[6px] md:-bottom-4">
          {error}
        </div>
      )}
    </div>
  );
});

export { TextArea };
